import React, { useRef, useState } from 'react';
import {
    Alert,
    Button,
    Checkbox,
    Group,
    Modal,
    Stack,
    Text,
    TextInput,
    ThemeIcon,
} from '@mantine/core';
import { CheckCircle, FileCsv, Warning, X } from '@phosphor-icons/react';

import { validateEntityAttributeValues, type EntityType, type ValidateValuesResponse } from '@api/entity-attributes';

interface AddColumnModalProps {
    opened: boolean;
    onClose: () => void;
    entityType: EntityType;
    /** Creates the attribute (returns ID), then optionally uploads the file. */
    onConfirm: (name: string, showInFilter: boolean, csvFile: File | null) => Promise<void>;
}

type Step = 'define' | 'csv' | 'confirm';

const AddColumnModal: React.FC<AddColumnModalProps> = ({
    opened,
    onClose,
    entityType,
    onConfirm,
}) => {
    const [step, setStep] = useState<Step>('define');
    const [name, setName] = useState('');
    const [showInFilter, setShowInFilter] = useState(false);
    const [nameError, setNameError] = useState<string | null>(null);

    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [validation, setValidation] = useState<ValidateValuesResponse | null>(null);
    const [validating, setValidating] = useState(false);
    const [csvError, setCsvError] = useState<string | null>(null);

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const reset = () => {
        setStep('define');
        setName('');
        setShowInFilter(false);
        setNameError(null);
        setCsvFile(null);
        setValidation(null);
        setValidating(false);
        setCsvError(null);
        setSaving(false);
        setSaveError(null);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    // Step 1 → 2
    const handleDefineNext = () => {
        if (!name.trim()) { setNameError('Column name is required'); return; }
        setNameError(null);
        setStep('csv');
    };

    // CSV file selected — validate immediately
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (fileInputRef.current) fileInputRef.current.value = '';
        setCsvError(null);
        setValidating(true);
        try {
            const result = await validateEntityAttributeValues(entityType, file);
            setCsvFile(file);
            setValidation(result);
        } catch {
            setCsvError('Failed to validate file. Make sure it matches the template format.');
        } finally {
            setValidating(false);
        }
    };

    const removeCsvFile = () => {
        setCsvFile(null);
        setValidation(null);
        setCsvError(null);
    };

    // Step 3 — create attribute + upload
    const handleConfirm = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            await onConfirm(name.trim(), showInFilter, csvFile);
            handleClose();
        } catch (err: any) {
            setSaveError(err?.message ?? 'Something went wrong. Please try again.');
            setSaving(false);
        }
    };

    const renderDefine = () => (
        <Stack gap="md">
            <TextInput
                label="Column name"
                placeholder="e.g. ABC Class"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                error={nameError}
                onKeyDown={(e) => { if (e.key === 'Enter') handleDefineNext(); }}
                data-autofocus
            />
            <Checkbox
                label="Show in filters"
                description="This column will appear as a filter option across the product."
                checked={showInFilter}
                onChange={(e) => setShowInFilter(e.currentTarget.checked)}
            />
            <Group justify="flex-end" mt="xs">
                <Button variant="subtle" color="gray" onClick={handleClose}>Cancel</Button>
                <Button onClick={handleDefineNext}>Next</Button>
            </Group>
        </Stack>
    );

    const renderCsv = () => (
        <Stack gap="md">
            <Text size="sm" c="dimmed">
                Optionally upload a CSV to pre-populate values for <strong>{name}</strong>.
                Include a header row; the first column is the entity key and the second is the value.
            </Text>

            {!csvFile && (
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    {csvError && (
                        <Alert color="red" icon={<Warning size={16} />}>{csvError}</Alert>
                    )}
                    <Button
                        variant="outline"
                        leftSection={<FileCsv size={16} />}
                        loading={validating}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Choose CSV file
                    </Button>
                </>
            )}

            {csvFile && validation && (
                <Alert
                    color={validation.unrecognizedEntityIds.length > 0 ? 'yellow' : 'green'}
                    icon={<FileCsv size={16} />}
                    title={csvFile.name}
                >
                    <Group justify="space-between" wrap="nowrap">
                        <Text size="xs">
                            {validation.parsedCount} rows parsed
                            {validation.missingEntityIds.length > 0 && ` · ${validation.missingEntityIds.length} entities will be skipped`}
                        </Text>
                        <Button
                            variant="transparent"
                            size="compact-xs"
                            color="gray"
                            onClick={removeCsvFile}
                        >
                            <X size={12} />
                        </Button>
                    </Group>
                </Alert>
            )}

            <Group justify="space-between" mt="xs">
                <Button variant="subtle" color="gray" onClick={() => setStep('define')}>Back</Button>
                <Group>
                    <Button variant="subtle" color="gray" onClick={() => { removeCsvFile(); setStep('confirm'); }}>
                        Skip for now
                    </Button>
                    <Button onClick={() => setStep('confirm')} disabled={validating}>
                        {csvFile ? 'Continue' : 'Skip & continue'}
                    </Button>
                </Group>
            </Group>
        </Stack>
    );

    const renderConfirm = () => (
        <Stack gap="md">
            <Stack gap="xs">
                <Group gap="xs">
                    <ThemeIcon color="blue" variant="light" size="sm">
                        <CheckCircle size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={500}>Column: <strong>{name}</strong></Text>
                </Group>
                <Text size="xs" c="dimmed" ml={30}>
                    {showInFilter ? 'Will appear as a filter option' : 'Not shown in filters'}
                </Text>
                {csvFile && validation && (
                    <Text size="xs" c="dimmed" ml={30}>
                        {validation.parsedCount - validation.unrecognizedEntityIds.length} values will be uploaded from {csvFile.name}
                    </Text>
                )}
                {!csvFile && (
                    <Text size="xs" c="dimmed" ml={30}>No initial values — you can add them later</Text>
                )}
            </Stack>

            {saveError && (
                <Alert color="red" icon={<Warning size={16} />}>{saveError}</Alert>
            )}

            <Group justify="space-between" mt="xs">
                <Button variant="subtle" color="gray" onClick={() => setStep('csv')}>Back</Button>
                <Button onClick={handleConfirm} loading={saving}>
                    Add property
                </Button>
            </Group>
        </Stack>
    );

    const stepTitle: Record<Step, string> = {
        define: 'Add column',
        csv: 'Add initial values (optional)',
        confirm: 'Confirm & create',
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={stepTitle[step]}
            size="sm"
            centered
        >
            {step === 'define' && renderDefine()}
            {step === 'csv' && renderCsv()}
            {step === 'confirm' && renderConfirm()}
        </Modal>
    );
};

export default AddColumnModal;
